import React, { ChangeEvent, FC } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { FloatingLabelInput } from 'components/floating-label-fields';
import IconAmex from 'components/icons/amex';
import IconDinersClub from 'components/icons/diners-club';
import IconDiscover from 'components/icons/discover';
import IconJCB from 'components/icons/jcb';
import IconMastercard from 'components/icons/mastercard';
import IconVisa from 'components/icons/visa';
import { CardType } from 'state/payment/types';
import { FormValidationState } from 'utils/form';
import { creditCardNumberMask, expiryMask } from 'utils/masks';
import { IPaymentErrors, IPaymentState } from 'utils/payment';

import { messages } from '../messages';

interface IFirstDataCreditCardFormInputs {
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  errors: IPaymentErrors;
  paymentValues: IPaymentState;
  supportedCardTypes?: CardType[];
}

export const CardNumberWrapper = styled.div`
  flex-basis: 100%;
  flex-grow: 1;
  position: relative;
  ${Styles.desktop`
    flex-basis: 50%;
  `}
`;

const CvvWrapper = styled.div`
  flex-basis: 40%;
  margin-left: 2.5%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 20%;
  `}
`;

const ExpiryWrapper = styled.div`
  flex-basis: 40%;
  margin-right: 2.5%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 20%;
  `}
`;

const ExpiryCVVWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const AcceptedCards = styled.div`
  position: absolute;
  right: 1rem;
  top: 1.6rem;
  z-index: ${Styles.zIndex.max - 300};

  svg {
    height: 1.6rem;
    width: 1.6rem;
    margin-right: 0.5rem;
  }

  > :last-child {
    margin-right: 0;
  }
`;

const CreditCardIcon: FC<{ cardType: string }> = ({ cardType }) =>
  ({
    AMEX: <IconAmex />,
    DISCOVER: <IconDiscover />,
    DINERS_CLUB: <IconDinersClub />,
    JCB: <IconJCB />,
    MASTERCARD: <IconMastercard />,
    VISA: <IconVisa />
  }[cardType] || null);

const FirstDataCreditCardInputs: FC<IFirstDataCreditCardFormInputs> = ({
  errors,
  onChange,
  paymentValues,
  supportedCardTypes = []
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <CardNumberWrapper>
        <AcceptedCards>
          {paymentValues.isCardNumberValid ? (
            <CreditCardIcon cardType={paymentValues.cardType} />
          ) : (
            <>
              {supportedCardTypes.map(
                // @TODO: Global team to uncomment in upcoming task
                () => null // <CreditCardIcon key={cardType} cardType={cardType} />
              )}
            </>
          )}
        </AcceptedCards>
        <FloatingLabelInput
          label={formatMessage(messages.creditCardNumber)}
          name="cardNumber"
          data-testid="cardNumber"
          type="text"
          inputMode="numeric"
          value={paymentValues.cardNumber}
          onChange={onChange}
          autocomplete="cc-number"
          validation={
            errors.cardType || errors.cardNumber ? FormValidationState.INVALID : undefined
          }
          message={errors.cardType || errors.cardNumber}
          required
          mask={creditCardNumberMask}
          guide
          placeholderChar=" "
        />
      </CardNumberWrapper>
      <ExpiryCVVWrapper>
        <ExpiryWrapper>
          <FloatingLabelInput
            label={formatMessage(messages.monthYearShort)}
            name="expiry"
            data-testid="expiry"
            aria-label={formatMessage(messages.cardExpiration)}
            type="text"
            inputMode="numeric"
            value={paymentValues.expiry}
            onChange={onChange}
            required
            autocomplete="cc-exp"
            validation={errors.expiry ? FormValidationState.INVALID : undefined}
            message={errors.expiry}
            mask={expiryMask}
            guide={false}
          />
        </ExpiryWrapper>
        <CvvWrapper>
          <FloatingLabelInput
            label={formatMessage(messages.cvv)}
            name="cvv"
            data-testid="cvv"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={paymentValues.cvv}
            onChange={onChange}
            maxLength="4"
            required
            autocomplete="cc-csc"
            validation={errors.cvv ? FormValidationState.INVALID : undefined}
            message={errors.cvv}
          />
        </CvvWrapper>
      </ExpiryCVVWrapper>
    </>
  );
};

export default FirstDataCreditCardInputs;
